import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  VisitCardResponse,
  RetryKoalendarDeliveriesResponse,
  AddStaffMemberRequest,
  AppointmentFavoritesResponse,
  GownPickerCatalog,
  SaveFavoritesRequest,
  AppointmentDetailResponse,
  AppointmentLinkResponse,
  AssignAppointmentRequest,
  CreateAppointmentRequest,
  CreateAppointmentResponse,
  RescheduleAppointmentRequest,
  SetAppointmentStatusRequest,
  SetFollowUpRequest,
  UpdateAppointmentRequest,
  ApplySyncRequest,
  BackOfficeSettings,
  BookingSettings,
  StaffMember,
  UpdateBackOfficeSettings,
  UpdateStaffMemberRequest,
  UpdateBookingSettings,
  GarmentResponse,
  LinkEnquiryRequest,
  LinkEnquiryResponse,
  RackItemResponse,
  RecordOutcomesRequest,
  SetGarmentStatusRequest,
  PublishStorefrontRequest,
  SetRackItemStatusRequest,
  StorefrontPlan,
  StorefrontRunResponse,
  SyncRunResponse,
  SyncScan,
  AddUpstreamOptionRequest,
  RemoveUpstreamWordRequest,
  SetUpstreamWordRequest,
  UpstreamVocabularyResponse,
  ThemeWorkspaceGrant,
  CreateOrderRequest,
  EmailInvoiceRequest,
  EmailInvoiceResponse,
  OrderDetailResponse,
  SupplierItemsResponse,
  OrderLineInput,
  RecordPaymentRequest,
  SetSalesStaffRequest,
  UpdateOrderLineRequest,
  UpdateOrderRequest,
  AdvanceFulfillmentRequest,
  ArrivalNoticeEmailRequest,
  ArrivalNoticeEmailResponse,
  RecordArrivalNoticeRequest,
  PickupOverrideRequest,
  UpdateLineTrackingRequest,
  CalculateCommissionResponse,
  CommissionPayoutResponse,
  DiscardPayoutResponse,
} from '@fitting-room/contracts'
import {
  ApiRequestError,
  assignAppointment,
  browseGowns,
  closeFollowUp,
  createAppointment,
  fetchAppointment,
  fetchVisitCard,
  chargeNoShowFee,
  fetchAppointmentFavorites,
  fetchAppointmentTypes,
  fetchFollowUps,
  mintAppointmentLink,
  rescheduleAppointment,
  saveAppointmentFavorites,
  setAppointmentStatus,
  setFollowUp,
  updateAppointment,
  type AppointmentFilters,
  addTeamMember,
  applyGownSync,
  fetchAssignees,
  fetchBackOffice,
  fetchFailedKoalendar,
  retryFailedKoalendar,
  fetchTeam,
  saveBackOffice,
  setTeamMemberActive,
  updateTeamMember,
  fetchCatalogue,
  fetchCompleteness,
  fetchEnquiries,
  fetchGownSyncRun,
  fetchPullSheet,
  fetchRack,
  fetchSettings,
  fetchStorefrontRun,
  fetchThemeHistory,
  fetchThemeWorkspace,
  fetchVisits,
  linkEnquiry,
  openThemeWorkspace,
  planStorefront,
  postOutcomes,
  publishStorefront,
  saveSettings,
  scanGownSync,
  fetchSyncWords,
  setSyncWord,
  addSyncWordOption,
  removeSyncWord,
  setGarmentStatus,
  setRackItemStatus,
  addOrderLine,
  createOrder,
  fetchOrder,
  fetchOrders,
  linkOrderAppointment,
  lookupGarments,
  lookupRack,
  removeOrderLine,
  recordPayment,
  voidPayment,
  setPaymentShopifyOrderNo,
  setAccountingChecked,
  fetchPaymentsReport,
  fetchShopifyRecentOrders,
  fetchSupplierItems,
  linkSupplierItem,
  refreshSupplierLines,
  unlinkSupplierItem,
  type ShopifyLookup,
  setOrderSalesStaff,
  setOrderStatus,
  unlinkOrderAppointment,
  updateOrder,
  updateOrderLine,
  type OrderFilters,
  advanceOrderLine,
  emailArrivalNotice,
  previewArrivalNotice,
  recordArrivalNotice,
  undoArrivalNotice,
  fetchAlerts,
  fetchMarketingVisits,
  fetchCommission,
  calculateCommission,
  updatePayoutNote,
  confirmPayout,
  discardPayout,
  overridePickup,
  undoOrderLine,
  updateLineTracking,
  emailInvoice,
  fetchInvoiceEmailStatus,
} from './api'
import { monthRange } from './months'

const noRetryOn4xx = (failureCount: number, error: unknown) => {
  if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false
  return failureCount < 2
}

export function useVisits(staffToken: string | null, date: string) {
  return useQuery({
    queryKey: ['admin', 'visits', 'day', date] as const,
    queryFn: ({ signal }) => fetchVisits(staffToken ?? '', { date }, signal),
    enabled: Boolean(staffToken) && Boolean(date),
    retry: noRetryOn4xx,
  })
}

/**
 * `sessionId` is part of the key, not just the request: an earlier version and
 * the current one are two different sheets for one visit, and caching them
 * under one key would show whichever was fetched last.
 */
export function usePullSheet(staffToken: string | null, visitId: string, sessionId?: string) {
  return useQuery({
    queryKey: ['admin', 'pull-sheet', visitId, sessionId ?? 'current'] as const,
    queryFn: ({ signal }) => fetchPullSheet(staffToken ?? '', visitId, sessionId, signal),
    enabled: Boolean(staffToken) && Boolean(visitId),
    retry: noRetryOn4xx,
  })
}

export function useCompleteness(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'completeness'] as const,
    queryFn: ({ signal }) => fetchCompleteness(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

export function useCatalogue(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'catalogue'] as const,
    queryFn: ({ signal }) => fetchCatalogue(staffToken ?? '', { limit: 1000 }, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

export function useRack(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'rack'] as const,
    queryFn: ({ signal }) => fetchRack(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * A stylist's own hand on a status.
 *
 * Both lists are invalidated rather than the one that changed, because the
 * catalogue health figures above the table are counted over statuses too — a
 * screen that showed a gown as active under a percentage that still counted it
 * as retired would be a screen nobody trusts twice.
 */
export function useSetGarmentStatus(staffToken: string | null, garmentId: string) {
  const client = useQueryClient()
  return useMutation<GarmentResponse, ApiRequestError, SetGarmentStatusRequest>({
    mutationFn: (body) => setGarmentStatus(staffToken ?? '', garmentId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'catalogue'] })
      void client.invalidateQueries({ queryKey: ['admin', 'completeness'] })
    },
  })
}

export function useSetRackItemStatus(staffToken: string | null, itemId: string) {
  const client = useQueryClient()
  return useMutation<RackItemResponse, ApiRequestError, SetRackItemStatusRequest>({
    mutationFn: (body) => setRackItemStatus(staffToken ?? '', itemId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'rack'] })
    },
  })
}

/* ------------------------------- sync gowns ------------------------------ */

/**
 * The scan, as a mutation rather than a query.
 *
 * It reads and writes nothing, so a query is the obvious fit and the wrong
 * one: a query refetches on its own schedule, and this one costs a real read
 * of the boutique's Airtable base against a five-request-a-second budget. It
 * must happen when — and only when — a stylist asks for it.
 */
export function useScanGownSync(staffToken: string | null) {
  return useMutation<SyncScan, ApiRequestError, void>({
    mutationFn: () => scanGownSync(staffToken ?? ''),
  })
}

export function useApplyGownSync(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<SyncRunResponse, ApiRequestError, ApplySyncRequest>({
    mutationFn: (body) => applyGownSync(staffToken ?? '', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'sync', 'run'] })
    },
  })
}

/**
 * The run, polled while it is in flight and left alone when it is not.
 *
 * This is what makes an apply survive a closed tab: the run belongs to the
 * boutique rather than to the page that started it, so reopening the console
 * finds it still going. `refetchInterval` returning `false` is how the polling
 * stops the moment it finishes — a console left open on a shop floor all day
 * must not spend the afternoon asking about a sync that ended at ten.
 */
export function useGownSyncRun(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'sync', 'run'] as const,
    queryFn: ({ signal }) => fetchGownSyncRun(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    refetchInterval: (query) => (query.state.data?.run?.status === 'running' ? 2000 : false),
  })
}

/* --------------------------- words from Airtable -------------------------- */

const SYNC_WORDS_KEY = ['admin', 'sync', 'words'] as const

/**
 * The words the sync knows. Owner-only on the server, so `enabled` is how a
 * screen a stylist can also open (the Catalog page) asks only when it may.
 */
export function useSyncWords(staffToken: string | null, enabled = true) {
  return useQuery({
    queryKey: SYNC_WORDS_KEY,
    queryFn: ({ signal }) => fetchSyncWords(staffToken ?? '', signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
  })
}

/**
 * One mutation for the three rulings, because the console treats them alike:
 * each answers with the whole list, which replaces the cached one, and the
 * caller re-scans so the word it just ruled on leaves the panel.
 */
export type SyncWordRuling =
  | { kind: 'set'; body: SetUpstreamWordRequest }
  | { kind: 'new-option'; body: AddUpstreamOptionRequest }
  | { kind: 'remove'; body: RemoveUpstreamWordRequest }

export function useRuleSyncWord(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<UpstreamVocabularyResponse, ApiRequestError, SyncWordRuling>({
    mutationFn: (ruling) => {
      const token = staffToken ?? ''
      if (ruling.kind === 'set') return setSyncWord(token, ruling.body)
      if (ruling.kind === 'new-option') return addSyncWordOption(token, ruling.body)
      return removeSyncWord(token, ruling.body)
    },
    onSuccess: (body) => {
      client.setQueryData(SYNC_WORDS_KEY, body)
      // A new option changes what the catalog's columns and filters can say.
      void client.invalidateQueries({ queryKey: ['admin', 'completeness'] })
    },
  })
}

/* ------------------------------ the storefront ---------------------------- */

/**
 * A mutation rather than a query, for the same reason the gown scan is one:
 * planning costs a full read of someone else's storefront against the cost
 * budget the publish itself has to come out of. It must happen when - and only
 * when - somebody asks for it.
 */
export function usePlanStorefront(staffToken: string | null) {
  return useMutation<StorefrontPlan, ApiRequestError, void>({
    mutationFn: () => planStorefront(staffToken ?? ''),
  })
}

export function usePublishStorefront(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<StorefrontRunResponse, ApiRequestError, PublishStorefrontRequest>({
    mutationFn: (body) => publishStorefront(staffToken ?? '', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'storefront', 'run'] })
    },
  })
}

/**
 * The publish, polled while it is in flight and left alone when it is not -
 * and the answer to whether this deployment has a storefront at all, which is
 * the first thing the page needs and the reason this query runs on load.
 */
export function useStorefrontRun(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'storefront', 'run'] as const,
    queryFn: ({ signal }) => fetchStorefrontRun(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    refetchInterval: (query) => (query.state.data?.run?.status === 'running' ? 2000 : false),
  })
}

/* ---------------------------- the theme workspace ------------------------ */

export function useThemeWorkspace(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'theme', 'workspace'] as const,
    queryFn: ({ signal }) => fetchThemeWorkspace(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * Minting a key is a mutation in the plain sense: a credential now exists
 * that did not. It invalidates nothing - the status page changes only when
 * the assistant writes something, which the history query is polled for.
 */
export function useOpenThemeWorkspace(staffToken: string | null) {
  return useMutation<ThemeWorkspaceGrant, ApiRequestError, void>({
    mutationFn: () => openThemeWorkspace(staffToken ?? ''),
  })
}

/** Polled while a key is out, so the owner watches the assistant's writes arrive. */
export function useThemeHistory(staffToken: string | null, watching: boolean) {
  return useQuery({
    queryKey: ['admin', 'theme', 'history'] as const,
    queryFn: ({ signal }) => fetchThemeHistory(staffToken ?? '', undefined, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    refetchInterval: watching ? 5000 : false,
  })
}

export function useRecordOutcomes(staffToken: string | null, visitId: string) {
  const client = useQueryClient()
  return useMutation<unknown, ApiRequestError, RecordOutcomesRequest>({
    mutationFn: (body) => postOutcomes(staffToken ?? '', visitId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'pull-sheet', visitId] })
      void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
    },
  })
}

export function useEnquiries(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'enquiries'] as const,
    queryFn: ({ signal }) => fetchEnquiries(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * Linking moves a session onto a visit, so both lists it appears on are stale
 * the moment it succeeds — the enquiry queue it leaves, and the day it lands on.
 */
export function useLinkEnquiry(staffToken: string | null, sessionId: string) {
  const client = useQueryClient()
  return useMutation<LinkEnquiryResponse, ApiRequestError, LinkEnquiryRequest>({
    mutationFn: (body) => linkEnquiry(staffToken ?? '', sessionId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'enquiries'] })
      void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
      // The list just attached is now that appointment's favorites.
      void client.invalidateQueries({ queryKey: ['admin', 'favorites'] })
    },
  })
}

/** Opening hours and the size chart — the owner's Settings page. */
export function useSettings(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'settings'] as const,
    queryFn: ({ signal }) => fetchSettings(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

export function useSaveSettings(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<BookingSettings, ApiRequestError, UpdateBookingSettings>({
    mutationFn: (body) => saveSettings(staffToken ?? '', body),
    onSuccess: (saved) => client.setQueryData(['admin', 'settings'], saved),
  })
}

/* ------------------------------ the team ---------------------------------- */

/** Settings → Team. Owner only; a stylist's pickers read `useAssignees`. */
export function useTeam(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'team'] as const,
    queryFn: ({ signal }) => fetchTeam(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * Every change to the team invalidates both lists: the owner's, and the
 * assignee list every picker and calendar mark reads from.
 */
function useInvalidateTeam() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['admin', 'team'] })
    void client.invalidateQueries({ queryKey: ['admin', 'assignees'] })
  }
}

export function useAddTeamMember(staffToken: string | null) {
  const invalidate = useInvalidateTeam()
  return useMutation<StaffMember, ApiRequestError, AddStaffMemberRequest>({
    mutationFn: (body) => addTeamMember(staffToken ?? '', body),
    onSuccess: invalidate,
  })
}

export function useUpdateTeamMember(staffToken: string | null, id: string) {
  const invalidate = useInvalidateTeam()
  return useMutation<StaffMember, ApiRequestError, UpdateStaffMemberRequest>({
    mutationFn: (body) => updateTeamMember(staffToken ?? '', id, body),
    onSuccess: invalidate,
  })
}

export function useSetTeamMemberActive(staffToken: string | null, id: string) {
  const invalidate = useInvalidateTeam()
  return useMutation<StaffMember, ApiRequestError, boolean>({
    mutationFn: (active) => setTeamMemberActive(staffToken ?? '', id, active),
    onSuccess: invalidate,
  })
}

/** Active members, names and calendar marks only — for assigning appointments and orders. */
export function useAssignees(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'assignees'] as const,
    queryFn: ({ signal }) => fetchAssignees(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/* -------------------------- back-office settings -------------------------- */

export function useBackOffice(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'back-office'] as const,
    queryFn: ({ signal }) => fetchBackOffice(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

export function useSaveBackOffice(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<BackOfficeSettings, ApiRequestError, UpdateBackOfficeSettings>({
    mutationFn: (body) => saveBackOffice(staffToken ?? '', body),
    onSuccess: (saved) => {
      client.setQueryData(['admin', 'back-office'], saved)
      // Which types are still offered is said here; the pickers read it off the types.
      void client.invalidateQueries({ queryKey: ['admin', 'appointment-types'] })
    },
  })
}

/** Koalendar deliveries that failed, for Settings. Owner only. */
export function useFailedKoalendar(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'koalendar-failed'] as const,
    queryFn: ({ signal }) => fetchFailedKoalendar(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/** Tries them all again; appointments made by it show up everywhere appointments do. */
export function useRetryFailedKoalendar(staffToken: string | null) {
  const client = useQueryClient()
  return useMutation<RetryKoalendarDeliveriesResponse, ApiRequestError>({
    mutationFn: () => retryFailedKoalendar(staffToken ?? ''),
    onSuccess: (result) => {
      client.setQueryData(['admin', 'koalendar-failed'], { failed: result.failed })
      void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
    },
  })
}

/* ------------------------------ appointments ------------------------------ */

/**
 * The list over a range of days, with the server-side filters in the key —
 * each combination is its own answer, and caching two under one key would
 * show whichever was fetched last.
 */
export function useAppointments(
  staffToken: string | null,
  range: { readonly from: string; readonly to: string },
  filters: AppointmentFilters,
) {
  return useQuery({
    queryKey: ['admin', 'visits', 'range', range.from, range.to, filters] as const,
    queryFn: ({ signal }) => fetchVisits(staffToken ?? '', range, signal, filters),
    enabled: Boolean(staffToken) && Boolean(range.from) && Boolean(range.to),
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

/**
 * The month behind the grid, filtered the same way as the list beside it and
 * cached under its own key: a month is not a superset the list could be
 * sliced out of — either may be the fresher of the two.
 */
export function useAppointmentsInMonth(staffToken: string | null, month: string, filters: AppointmentFilters) {
  const { from, to } = monthRange(month)
  return useQuery({
    queryKey: ['admin', 'visits', 'month', month, filters] as const,
    queryFn: ({ signal }) => fetchVisits(staffToken ?? '', { from, to }, signal, filters),
    enabled: Boolean(staffToken) && Boolean(month),
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

/** The card on file for an appointment booked online, if there is one. `docs/23`. */
export function useVisitCard(staffToken: string | null, visitId: string) {
  return useQuery({
    queryKey: ['admin', 'visit-card', visitId] as const,
    queryFn: ({ signal }) => fetchVisitCard(staffToken ?? '', visitId, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

export function useChargeNoShowFee(staffToken: string | null, visitId: string) {
  const client = useQueryClient()
  return useMutation<Pick<VisitCardResponse, 'card'>, ApiRequestError, void>({
    mutationFn: () => chargeNoShowFee(staffToken ?? '', visitId),
    onSuccess: (body) => {
      client.setQueryData<VisitCardResponse>(['admin', 'visit-card', visitId], (old) => (old ? { ...old, card: body.card } : old))
    },
  })
}

export function useAppointment(staffToken: string | null, visitId: string | null) {
  return useQuery({
    queryKey: ['admin', 'visit', visitId] as const,
    queryFn: ({ signal }) => fetchAppointment(staffToken ?? '', visitId ?? '', signal),
    enabled: Boolean(staffToken) && Boolean(visitId),
    retry: noRetryOn4xx,
  })
}

/**
 * Her favorites, for the drawer. Keyed apart from `['admin', 'visit']` on
 * purpose: no write to the appointment changes what she picked, so the
 * invalidation every such write does would only refetch them for nothing.
 *
 * What does change them happens elsewhere — the customer picking on her own
 * phone, or an inquiry list attached to the appointment (`useLinkEnquiry`
 * invalidates this key). The first the console never hears about, so the
 * app-wide 30-second `staleTime` is turned off here and opening the drawer
 * always reads them afresh; the request resolves only the picked gowns.
 */
export function useAppointmentFavorites(staffToken: string | null, visitId: string | null) {
  return useQuery({
    queryKey: ['admin', 'favorites', visitId] as const,
    queryFn: ({ signal }) => fetchAppointmentFavorites(staffToken ?? '', visitId ?? '', signal),
    enabled: Boolean(staffToken) && Boolean(visitId),
    staleTime: 0,
    retry: noRetryOn4xx,
  })
}

/**
 * The favorites picker's Save. The answer is the list as it now stands, so
 * it goes straight into the drawer's cache — the section redraws in place
 * with no second request. The appointment is refetched too: a Save on an
 * appointment with no fitting room opens one, and the drawer's "Fitting room
 * link" and the list's status chip read that off the visit.
 */
export function useSaveAppointmentFavorites(staffToken: string | null, visitId: string) {
  const client = useQueryClient()
  return useMutation<AppointmentFavoritesResponse, ApiRequestError, SaveFavoritesRequest>({
    mutationFn: (body) => saveAppointmentFavorites(staffToken ?? '', visitId, body),
    onSuccess: (saved) => {
      client.setQueryData(['admin', 'favorites', visitId], saved)
      void client.invalidateQueries({ queryKey: ['admin', 'visit', visitId] })
      void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
      void client.invalidateQueries({ queryKey: ['admin', 'pull-sheet', visitId] })
    },
  })
}

/** One page of the favorites picker's catalog. The previous page stays up while the next loads, as tperent's picker does. */
export function useGownPicker(staffToken: string | null, params: { catalog: GownPickerCatalog; q: string; page: number }, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'gowns', params.catalog, params.q, params.page] as const,
    queryFn: ({ signal }) => browseGowns(staffToken ?? '', params, signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

/** The appointment types change with a deploy, not with the day, so they are read once — and again when Settings stops offering one. */
export function useAppointmentTypes(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'appointment-types'] as const,
    queryFn: ({ signal }) => fetchAppointmentTypes(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    staleTime: Infinity,
  })
}

export function useFollowUps(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'follow-ups'] as const,
    queryFn: ({ signal }) => fetchFollowUps(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * Every write to an appointment can change what each of these shows — the
 * lists it sits on, its own detail, the follow-up queue and the pull sheet's
 * header — so all of them are invalidated together rather than each mutation
 * guessing which one it touched.
 */
function useInvalidateAppointment() {
  const client = useQueryClient()
  return (detail?: AppointmentDetailResponse) => {
    if (detail) client.setQueryData(['admin', 'visit', detail.visit.id], detail)
    void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
    void client.invalidateQueries({ queryKey: ['admin', 'visit'] })
    void client.invalidateQueries({ queryKey: ['admin', 'follow-ups'] })
    void client.invalidateQueries({ queryKey: ['admin', 'pull-sheet'] })
    void client.invalidateQueries({ queryKey: ['admin', 'alerts'] })
  }
}

export function useCreateAppointment(staffToken: string | null) {
  const invalidate = useInvalidateAppointment()
  return useMutation<CreateAppointmentResponse, ApiRequestError, CreateAppointmentRequest>({
    mutationFn: (body) => createAppointment(staffToken ?? '', body),
    onSuccess: () => invalidate(),
  })
}

export function useUpdateAppointment(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, UpdateAppointmentRequest>({
    mutationFn: (body) => updateAppointment(staffToken ?? '', visitId, body),
    onSuccess: invalidate,
  })
}

export function useRescheduleAppointment(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, RescheduleAppointmentRequest>({
    mutationFn: (body) => rescheduleAppointment(staffToken ?? '', visitId, body),
    onSuccess: invalidate,
  })
}

export function useAssignAppointment(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, AssignAppointmentRequest>({
    mutationFn: (body) => assignAppointment(staffToken ?? '', visitId, body),
    onSuccess: invalidate,
  })
}

export function useSetAppointmentStatus(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, SetAppointmentStatusRequest>({
    mutationFn: (body) => setAppointmentStatus(staffToken ?? '', visitId, body),
    onSuccess: invalidate,
  })
}

/** The outcome and the reason, through the pull sheet's own endpoint. */
export function useRecordAppointmentOutcome(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<unknown, ApiRequestError, RecordOutcomesRequest>({
    mutationFn: (body) => postOutcomes(staffToken ?? '', visitId, body),
    onSuccess: () => invalidate(),
  })
}

export function useSetFollowUp(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, SetFollowUpRequest>({
    mutationFn: (body) => setFollowUp(staffToken ?? '', visitId, body),
    onSuccess: invalidate,
  })
}

export function useCloseFollowUp(staffToken: string | null, visitId: string) {
  const invalidate = useInvalidateAppointment()
  return useMutation<AppointmentDetailResponse, ApiRequestError, 'done' | 'clear'>({
    mutationFn: (how) => closeFollowUp(staffToken ?? '', visitId, how),
    onSuccess: invalidate,
  })
}

/** Mints a credential, so a mutation: nothing to invalidate, and never refetched by itself. */
export function useMintAppointmentLink(staffToken: string | null, visitId: string) {
  return useMutation<AppointmentLinkResponse, ApiRequestError, void>({
    mutationFn: () => mintAppointmentLink(staffToken ?? '', visitId),
  })
}

/* --------------------------------- orders --------------------------------- */

export function useOrders(staffToken: string | null, filters: OrderFilters) {
  return useQuery({
    queryKey: ['admin', 'orders', 'list', filters] as const,
    queryFn: ({ signal }) => fetchOrders(staffToken ?? '', filters, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

export function useOrder(staffToken: string | null, orderId: string | null | undefined) {
  return useQuery({
    queryKey: ['admin', 'order', orderId] as const,
    queryFn: ({ signal }) => fetchOrder(staffToken ?? '', orderId ?? '', signal),
    enabled: Boolean(staffToken) && Boolean(orderId),
    retry: noRetryOn4xx,
  })
}

/**
 * Every write to an order answers with the whole order, which goes straight
 * into its cache. The list, the appointments it links (their drawer shows
 * the order) and the rack (a piece held or released) are invalidated around it.
 */
function useApplyOrder() {
  const client = useQueryClient()
  return (detail: OrderDetailResponse) => {
    client.setQueryData(['admin', 'order', detail.order.id], detail)
    void client.invalidateQueries({ queryKey: ['admin', 'orders'] })
    void client.invalidateQueries({ queryKey: ['admin', 'visit'] })
    void client.invalidateQueries({ queryKey: ['admin', 'visits'] })
    void client.invalidateQueries({ queryKey: ['admin', 'rack'] })
    void client.invalidateQueries({ queryKey: ['admin', 'order-lookup'] })
    void client.invalidateQueries({ queryKey: ['admin', 'alerts'] })
    // A payment, a void or a line edit moves an order on or off the commission screen.
    void client.invalidateQueries({ queryKey: ['admin', 'commission'] })
  }
}

export function useCreateOrder(staffToken: string | null) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, CreateOrderRequest>({
    mutationFn: (body) => createOrder(staffToken ?? '', body),
    onSuccess: (detail) => {
      apply(detail)
      // A Shopify order just picked for its payment is "already recorded" now.
      void client.invalidateQueries({ queryKey: ['admin', 'shopify-orders'] })
    },
  })
}

export function useUpdateOrder(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, UpdateOrderRequest>({
    mutationFn: (body) => updateOrder(staffToken ?? '', orderId, body),
    onSuccess: apply,
  })
}

export function useSetOrderSalesStaff(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, SetSalesStaffRequest>({
    mutationFn: (body) => setOrderSalesStaff(staffToken ?? '', orderId, body),
    onSuccess: apply,
  })
}

export function useAddOrderLine(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, OrderLineInput>({
    mutationFn: (body) => addOrderLine(staffToken ?? '', orderId, body),
    onSuccess: apply,
  })
}

export function useUpdateOrderLine(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, UpdateOrderLineRequest>({
    mutationFn: (body) => updateOrderLine(staffToken ?? '', orderId, lineId, body),
    onSuccess: apply,
  })
}

export function useRemoveOrderLine(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, { lineId: string; reason: string }>({
    mutationFn: ({ lineId, reason }) => removeOrderLine(staffToken ?? '', orderId, lineId, reason),
    onSuccess: apply,
  })
}

export function useSetOrderStatus(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, { to: 'cancel' | 'restore'; reason: string }>({
    mutationFn: ({ to, reason }) => setOrderStatus(staffToken ?? '', orderId, to, reason),
    onSuccess: apply,
  })
}

/** The Jovita page. Fetched as it opens, which also catches linked lines up (server side, at most every ten minutes). */
export function useSupplierItems(staffToken: string | null) {
  return useQuery<SupplierItemsResponse, ApiRequestError>({
    queryKey: ['admin', 'supplier', 'items'] as const,
    queryFn: ({ signal }) => fetchSupplierItems(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}

/**
 * Catch linked special orders up with the Jovita OMS as a page opens — the
 * orders list, an order. In the background: the page does not wait, and when
 * something moved, what shows it is fetched again. The server does nothing
 * within ten minutes of the last refresh, and nothing when Jovita isn't
 * connected; a failure here is not the page's to show.
 */
export function useSupplierRefreshOnOpen(staffToken: string | null) {
  const client = useQueryClient()
  useEffect(() => {
    if (!staffToken) return
    let live = true
    refreshSupplierLines(staffToken)
      .then((result) => {
        if (!live || result.updated === 0) return
        void client.invalidateQueries({ queryKey: ['admin', 'orders'] })
        void client.invalidateQueries({ queryKey: ['admin', 'order'] })
        void client.invalidateQueries({ queryKey: ['admin', 'alerts'] })
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [staffToken, client])
}

export function useLinkSupplierItem(staffToken: string | null) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, { orderId: string; lineId: string; itemId: string }>({
    mutationFn: ({ orderId, lineId, itemId }) => linkSupplierItem(staffToken ?? '', orderId, lineId, itemId),
    onSuccess: (detail) => {
      apply(detail)
      void client.invalidateQueries({ queryKey: ['admin', 'supplier'] })
    },
  })
}

export function useUnlinkSupplierItem(staffToken: string | null) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, { orderId: string; lineId: string }>({
    mutationFn: ({ orderId, lineId }) => unlinkSupplierItem(staffToken ?? '', orderId, lineId),
    onSuccess: (detail) => {
      apply(detail)
      void client.invalidateQueries({ queryKey: ['admin', 'supplier'] })
    },
  })
}

export function useLinkOrderAppointment(staffToken: string | null) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, { orderId: string; visitId: string }>({
    mutationFn: ({ orderId, visitId }) => linkOrderAppointment(staffToken ?? '', orderId, { visitId }),
    onSuccess: apply,
  })
}

export function useUnlinkOrderAppointment(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, string>({
    mutationFn: (visitId) => unlinkOrderAppointment(staffToken ?? '', orderId, visitId),
    onSuccess: apply,
  })
}

/** Made-to-order styles for the line picker. Short-lived: the catalog changes by sync, not by the minute. */
export function useGarmentLookup(staffToken: string | null, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'order-lookup', 'garments', q] as const,
    queryFn: ({ signal }) => lookupGarments(staffToken ?? '', q, signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

/** Rack pieces for the line picker, with what each is doing. Fresh every time: a piece is held by the minute. */
export function useRackLookup(staffToken: string | null, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'order-lookup', 'rack', q] as const,
    queryFn: ({ signal }) => lookupRack(staffToken ?? '', q, signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    staleTime: 0,
    placeholderData: (previous) => previous,
  })
}

/* -------------------------------- payments -------------------------------- */

export function useRecordPayment(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, RecordPaymentRequest>({
    mutationFn: (body) => recordPayment(staffToken ?? '', orderId, body),
    onSuccess: (detail) => {
      apply(detail)
      // A Shopify order just picked is "already recorded" now.
      void client.invalidateQueries({ queryKey: ['admin', 'shopify-orders'] })
      void client.invalidateQueries({ queryKey: ['admin', 'payments-report'] })
    },
  })
}

export function useVoidPayment(staffToken: string | null) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, { paymentId: string; reason: string }>({
    mutationFn: ({ paymentId, reason }) => voidPayment(staffToken ?? '', paymentId, reason),
    onSuccess: (detail) => {
      apply(detail)
      void client.invalidateQueries({ queryKey: ['admin', 'shopify-orders'] })
      void client.invalidateQueries({ queryKey: ['admin', 'payments-report'] })
    },
  })
}

/** A payment's Shopify order number, typed or corrected after it was recorded. */
export function useSetPaymentShopifyOrderNo(staffToken: string | null) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<OrderDetailResponse, ApiRequestError, { paymentId: string; shopifyOrderNo: string | null }>({
    mutationFn: ({ paymentId, shopifyOrderNo }) => setPaymentShopifyOrderNo(staffToken ?? '', paymentId, shopifyOrderNo),
    onSuccess: (detail) => {
      apply(detail)
      void client.invalidateQueries({ queryKey: ['admin', 'payments-report'] })
    },
  })
}

/** Whether this deployment can send email, so the invoice page can say so before anyone presses Send. */
export function useInvoiceEmailStatus(staffToken: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'invoice-email'] as const,
    queryFn: ({ signal }) => fetchInvoiceEmailStatus(staffToken ?? '', signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
  })
}

/** Emails the invoice. A send that went out comes back with the order, its history carrying the entry. */
export function useEmailInvoice(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<EmailInvoiceResponse, ApiRequestError, EmailInvoiceRequest>({
    mutationFn: (body) => emailInvoice(staffToken ?? '', orderId, body),
    onSuccess: (result) => {
      if (result.detail) apply(result.detail)
      // Sent, but answered without the order (its history entry, or the
      // read after it, failed): fetch it, so the history isn't the old one.
      else if (result.sent) void client.invalidateQueries({ queryKey: ['admin', 'order', orderId] })
    },
  })
}

export function useSetAccountingChecked(staffToken: string | null, orderId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, boolean>({
    mutationFn: (checked) => setAccountingChecked(staffToken ?? '', orderId, checked),
    onSuccess: apply,
  })
}

/**
 * The owner's report. Never served from cache, as tperent's isn't: this is
 * the screen a drawer is counted against, and yesterday's total shown while
 * today's loads is a wrong number on the one screen that must not have one.
 */
export function usePaymentsReport(staffToken: string | null, range: { from: string; to: string }, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'payments-report', range] as const,
    queryFn: ({ signal }) => fetchPaymentsReport(staffToken ?? '', range, signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    gcTime: 0,
  })
}

/** Recent Shopify orders for the record-payment dialog. Fresh every time: the charge was run a minute ago. */
export function useShopifyRecentOrders(staffToken: string | null, lookup: ShopifyLookup, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'shopify-orders', lookup] as const,
    queryFn: ({ signal }) => fetchShopifyRecentOrders(staffToken ?? '', lookup, signal),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    // Each look is an Admin API call. A minute is fresh enough to pick from,
    // and recording a payment or writing an order clears it anyway.
    staleTime: 60_000,
  })
}

/* ---------------------------- pickup and arrival --------------------------- */

export function useAdvanceOrderLine(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, AdvanceFulfillmentRequest>({
    mutationFn: (body) => advanceOrderLine(staffToken ?? '', orderId, lineId, body),
    onSuccess: apply,
  })
}

export function useUndoOrderLine(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, string>({
    mutationFn: (reason) => undoOrderLine(staffToken ?? '', orderId, lineId, reason),
    onSuccess: apply,
  })
}

export function useUpdateLineTracking(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, UpdateLineTrackingRequest>({
    mutationFn: (body) => updateLineTracking(staffToken ?? '', orderId, lineId, body),
    onSuccess: apply,
  })
}

/** Emails the customer that the line arrived; a send that went out comes back with the order. */
export function useEmailArrivalNotice(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  const client = useQueryClient()
  return useMutation<ArrivalNoticeEmailResponse, ApiRequestError, ArrivalNoticeEmailRequest>({
    mutationFn: (body) => emailArrivalNotice(staffToken ?? '', orderId, lineId, body),
    onSuccess: (result) => {
      if (result.detail) apply(result.detail)
      else if (result.sent) void client.invalidateQueries({ queryKey: ['admin', 'order', orderId] })
    },
  })
}

/** The arrival email as it would go, with the note as typed so far. Writes nothing. */
export function useArrivalNoticePreview(staffToken: string | null, orderId: string, lineId: string, message: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'arrival-preview', orderId, lineId, message] as const,
    queryFn: () => previewArrivalNotice(staffToken ?? '', orderId, lineId, message ? { message } : {}),
    enabled: Boolean(staffToken) && enabled,
    retry: noRetryOn4xx,
    staleTime: 60_000,
  })
}

export function useRecordArrivalNotice(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, RecordArrivalNoticeRequest>({
    mutationFn: (body) => recordArrivalNotice(staffToken ?? '', orderId, lineId, body),
    onSuccess: apply,
  })
}

export function useUndoArrivalNotice(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, void>({
    mutationFn: () => undoArrivalNotice(staffToken ?? '', orderId, lineId),
    onSuccess: apply,
  })
}

export function useOverridePickup(staffToken: string | null, orderId: string, lineId: string) {
  const apply = useApplyOrder()
  return useMutation<OrderDetailResponse, ApiRequestError, PickupOverrideRequest>({
    mutationFn: (body) => overridePickup(staffToken ?? '', orderId, lineId, body),
    onSuccess: apply,
  })
}

/**
 * Every alert in the boutique. The nav's count reads this too, so it is
 * fetched once per screen and shared; a write to an order or an appointment
 * invalidates it, and it refreshes itself every few minutes because "today"
 * moves on its own — a follow-up becomes due at midnight with nobody touching it.
 */
export function useAlerts(staffToken: string | null) {
  return useQuery({
    queryKey: ['admin', 'alerts'] as const,
    queryFn: ({ signal }) => fetchAlerts(staffToken ?? '', signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  })
}

/* -------------------------------- commission ------------------------------- */

/** The owner's commission screen, one page of the confirmed history at a time. */
export function useCommission(staffToken: string | null, page: { limit: number; offset: number }) {
  return useQuery({
    queryKey: ['admin', 'commission', page] as const,
    queryFn: ({ signal }) => fetchCommission(staffToken ?? '', page, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
    placeholderData: (previous) => previous,
  })
}

/**
 * Every commission write redraws the whole screen — an order moves between
 * the eligible list and a payout — and the orders it touched, whose pages
 * say which payout they are in.
 */
function useInvalidateCommission() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['admin', 'commission'] })
    void client.invalidateQueries({ queryKey: ['admin', 'order'] })
  }
}

export function useCalculateCommission(staffToken: string | null) {
  const invalidate = useInvalidateCommission()
  return useMutation<CalculateCommissionResponse, ApiRequestError, readonly string[]>({
    mutationFn: (orderIds) => calculateCommission(staffToken ?? '', orderIds),
    onSuccess: invalidate,
  })
}

export function useUpdatePayoutNote(staffToken: string | null, payoutId: string) {
  const invalidate = useInvalidateCommission()
  return useMutation<CommissionPayoutResponse, ApiRequestError, string>({
    mutationFn: (note) => updatePayoutNote(staffToken ?? '', payoutId, note),
    onSuccess: invalidate,
  })
}

export function useConfirmPayout(staffToken: string | null, payoutId: string) {
  const invalidate = useInvalidateCommission()
  return useMutation<CommissionPayoutResponse, ApiRequestError, string>({
    mutationFn: (note) => confirmPayout(staffToken ?? '', payoutId, note),
    onSuccess: invalidate,
  })
}

export function useDiscardPayout(staffToken: string | null, payoutId: string) {
  const invalidate = useInvalidateCommission()
  return useMutation<DiscardPayoutResponse, ApiRequestError, void>({
    mutationFn: () => discardPayout(staffToken ?? '', payoutId),
    onSuccess: invalidate,
  })
}

/** The marketing view (`GET /api/admin/marketing/visits`): every appointment in a month, as a marketing partner sees it. */
export function useMarketingVisits(staffToken: string | null, month: string) {
  const { from, to } = monthRange(month)
  return useQuery({
    queryKey: ['admin', 'marketing', 'visits', from, to] as const,
    queryFn: ({ signal }) => fetchMarketingVisits(staffToken ?? '', from, to, signal),
    enabled: Boolean(staffToken),
    retry: noRetryOn4xx,
  })
}
